import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, Building2, RefreshCw, PhoneCall, CheckCircle2, Archive, BadgeCheck, Code2 } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_STYLE = {
  new: 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300',
  contacted: 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300',
  qualified: 'bg-violet-100 text-violet-700 dark:bg-violet-900/40 dark:text-violet-300',
  converted: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300',
  archived: 'bg-slate-100 text-slate-500 dark:bg-cn-ink-700 dark:text-slate-400',
};
const FILTERS = [
  { value: 'new', label: 'New' },
  { value: 'contacted', label: 'Contacted' },
  { value: 'qualified', label: 'Qualified' },
  { value: 'converted', label: 'Converted' },
  { value: 'archived', label: 'Archived' },
  { value: 'all', label: 'All' },
];

const Tile = ({ testid, label, value, tone }) => (
  <div data-testid={testid} className="p-4 rounded-xl border border-slate-200 dark:border-cn-ink-700 bg-white dark:bg-cn-ink-800">
    <p className="text-xs text-slate-500 dark:text-slate-400">{label}</p>
    <p className={`text-2xl font-bold font-display mt-1 ${tone}`}>{value}</p>
  </div>
);

export const InstitutionInquiriesPanel = () => {
  const { token } = useAuth();
  const [data, setData] = useState({ inquiries: [], summary: {} });
  const [filter, setFilter] = useState('new');
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const authHeader = { headers: { Authorization: `Bearer ${token}` } };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const q = filter === 'all' ? '' : `?status=${filter}`;
      const { data } = await axios.get(`${API}/admin/institution-inquiries${q}`, { headers: { Authorization: `Bearer ${token}` } });
      setData(data);
    } catch (e) {
      toast.error('Failed to load inquiries');
    } finally {
      setLoading(false);
    }
  }, [token, filter]);

  useEffect(() => { const t = setTimeout(load, 0); return () => clearTimeout(t); }, [load]);

  const setStatus = async (it, status) => {
    setBusyId(it.id);
    const prev = it.status;
    try {
      await axios.patch(`${API}/admin/institution-inquiries/${it.id}`, { status }, authHeader);
      // Optimistically reflect the move in the summary tiles immediately…
      setData((d) => {
        const summary = { ...(d.summary || {}) };
        if (prev && summary[prev] != null) summary[prev] = Math.max(0, summary[prev] - 1);
        summary[status] = (summary[status] || 0) + 1;
        return { ...d, summary };
      });
      toast.success(`${it.org_name} marked ${status}`);
      await load(); // …then reconcile authoritatively
    } catch (e) {
      toast.error('Could not update inquiry');
    } finally {
      setBusyId(null);
    }
  };

  const s = data.summary || {};

  return (
    <div data-testid="institution-inquiries-panel">
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 mb-5" data-testid="institutions-summary">
        <Tile testid="inst-stat-new" label="New" value={s.new ?? 0} tone="text-blue-600" />
        <Tile testid="inst-stat-contacted" label="Contacted" value={s.contacted ?? 0} tone="text-amber-600" />
        <Tile testid="inst-stat-qualified" label="Qualified" value={s.qualified ?? 0} tone="text-violet-600" />
        <Tile testid="inst-stat-converted" label="Converted" value={s.converted ?? 0} tone="text-emerald-600" />
        <Tile testid="inst-stat-archived" label="Archived" value={s.archived ?? 0} tone="text-slate-500" />
      </div>

      <div className="flex items-center justify-between gap-2 flex-wrap mb-4">
        <div className="flex gap-1.5 flex-wrap">
          {FILTERS.map((f) => (
            <button key={f.value} onClick={() => setFilter(f.value)} data-testid={`inst-filter-${f.value}`}
              className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${filter === f.value ? 'bg-cn-primary text-white' : 'bg-slate-100 dark:bg-cn-ink-700 text-slate-600 dark:text-slate-300'}`}>
              {f.label}
            </button>
          ))}
        </div>
        <button onClick={load} data-testid="inst-refresh-btn"
          className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium bg-slate-100 dark:bg-cn-ink-700 text-slate-600 dark:text-slate-300 hover:text-cn-primary">
          <RefreshCw className="w-3.5 h-3.5" /> Refresh
        </button>
      </div>

      {loading ? (
        <div className="py-12 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-cn-primary" /></div>
      ) : data.inquiries.length === 0 ? (
        <div className="py-12 text-center" data-testid="institutions-empty">
          <Building2 className="w-12 h-12 text-slate-400 mx-auto mb-3" />
          <h3 className="text-lg font-semibold text-slate-900 dark:text-white">No inquiries in this view</h3>
          <p className="text-slate-500 dark:text-slate-400 mt-1">Demo &amp; API-access requests from the Institutions page land here.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {data.inquiries.map((it) => {
            const busy = busyId === it.id;
            return (
              <div key={it.id} className="rounded-xl border border-slate-200 dark:border-cn-ink-700 bg-white dark:bg-cn-ink-800 p-4" data-testid={`inst-row-${it.id}`}>
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="font-semibold text-slate-900 dark:text-white">{it.org_name}</p>
                      <span className={`px-2.5 py-0.5 rounded-full text-[11px] font-bold ${STATUS_STYLE[it.status] || ''}`}>{it.status}</span>
                      <span className="inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full bg-cn-soft text-cn-primary border border-cn-border">
                        {it.interest === 'api_access' ? <><Code2 className="w-3 h-3" /> API access</> : <><BadgeCheck className="w-3 h-3" /> Demo</>}
                      </span>
                    </div>
                    <p className="text-sm text-slate-600 dark:text-slate-300 mt-1">
                      {it.contact_name}{it.role ? ` · ${it.role}` : ''} — <a href={`mailto:${it.email}`} className="text-cn-primary">{it.email}</a>
                    </p>
                    <div className="flex flex-wrap gap-x-4 gap-y-1 mt-1.5 text-xs text-slate-400">
                      {it.facility_type && <span>{it.facility_type}</span>}
                      {it.monthly_discharges && <span>{it.monthly_discharges}</span>}
                      {it.phone && <span>{it.phone}</span>}
                      <span>{(it.created_at || '').slice(0, 10)}</span>
                    </div>
                    {it.message && <p className="text-sm text-slate-600 dark:text-slate-300 mt-2 italic">&ldquo;{it.message}&rdquo;</p>}
                  </div>
                  <div className="flex items-center gap-1.5">
                    {it.status !== 'contacted' && it.status !== 'converted' && (
                      <button onClick={() => setStatus(it, 'contacted')} disabled={busy} title="Mark contacted" data-testid={`inst-contacted-${it.id}`}
                        className="p-1.5 rounded-lg bg-amber-50 text-amber-700 hover:bg-amber-100 dark:bg-amber-900/30 dark:text-amber-300 disabled:opacity-50">
                        {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <PhoneCall className="w-3.5 h-3.5" />}
                      </button>
                    )}
                    {it.status !== 'qualified' && it.status !== 'converted' && (
                      <button onClick={() => setStatus(it, 'qualified')} disabled={busy} title="Mark qualified" data-testid={`inst-qualified-${it.id}`}
                        className="p-1.5 rounded-lg bg-violet-50 text-violet-700 hover:bg-violet-100 dark:bg-violet-900/30 dark:text-violet-300 disabled:opacity-50">
                        <BadgeCheck className="w-3.5 h-3.5" />
                      </button>
                    )}
                    {it.status !== 'converted' && (
                      <button onClick={() => setStatus(it, 'converted')} disabled={busy} title="Mark converted" data-testid={`inst-converted-${it.id}`}
                        className="p-1.5 rounded-lg bg-emerald-50 text-emerald-700 hover:bg-emerald-100 dark:bg-emerald-900/30 dark:text-emerald-300 disabled:opacity-50">
                        <CheckCircle2 className="w-3.5 h-3.5" />
                      </button>
                    )}
                    {it.status !== 'archived' && (
                      <button onClick={() => setStatus(it, 'archived')} disabled={busy} title="Archive" data-testid={`inst-archived-${it.id}`}
                        className="p-1.5 rounded-lg bg-slate-100 text-slate-500 hover:text-red-600 dark:bg-cn-ink-700 disabled:opacity-50">
                        <Archive className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default InstitutionInquiriesPanel;
